import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { api } from '@aeon-karaoke/cloud-convex';
import type { Doc } from '@aeon-karaoke/cloud-convex/_generated/dataModel';
import { MonoLabel, StatusDot, type StatusTone } from '@/component';
import { useLyricsPreview, useTrackJob, type TrackView } from '@/hook';
import { convex } from '@/lib/convex';
import { toResult } from '@/lib/result-adapter';
import { FixLyricsModal } from './FixLyricsModal';

type LyricsSource = NonNullable<TrackView['lyricsSource']>;

interface TrackLyricsPanelProps {
  track: TrackView;
  owned: boolean;
  /** "HUNTR/X · Golden", the song the track is matched to. */
  song: string;
}

const percent = (share: number) => `${Math.round(share * 100)}%`;

/** ", 1.1 s shorter than this recording" when the entry's length differs. */
function lengthNote(source: LyricsSource): string {
  const delta = source.durationDeltaMs;
  if (delta === undefined || Math.abs(delta) < 500) return '';
  return `, ${(Math.abs(delta) / 1000).toFixed(1)} s ${delta < 0 ? 'shorter' : 'longer'} than this recording`;
}

/** Where the words came from, in a sentence or two. */
function describeSource(source: LyricsSource | undefined, hasLyrics: boolean, song: string): string {
  if (!source) return hasLyrics ? 'Word-timed.' : 'The words arrive when the track is built.';
  const placed = source.fit ? ` ${percent(source.fit.placed)} of the words placed against the vocal.` : '';
  switch (source.provider) {
    case 'transcription':
      return source.unverified
        ? `The words we found for this song did not fit the singing (${source.fit ? percent(source.fit.placed) : 'few'} placed), so these are transcribed from the vocal. Check them, or pick the right entry.`
        : `Transcribed from the vocal; no published words were found for ${song}.`;
    case 'paste':
      return `Pasted words, timed against the vocal.${placed}`;
    case 'lrclib':
      return `From LRCLIB, ${source.synced ? 'synced' : 'plain'} entry for ${song}${lengthNote(source)}.${placed}`;
    case 'genius':
      return `From Genius, for ${song}.${placed}`;
  }
}

/** The chip beside the heading: the rebuild first, then the words' state. */
function status(track: TrackView, rebuilding: boolean): { label: string; tone: 'up' | 'acc' | 'down' | 'faint'; dot: StatusTone } {
  if (rebuilding) return { label: 'Re-timing', tone: 'acc', dot: 'preparing' };
  if (!track.hasLyrics) return { label: 'Not yet', tone: 'faint', dot: 'idle' };
  if (track.lyricsSource?.unverified) return { label: 'Unverified', tone: 'down', dot: 'attention' };
  return { label: 'Word-timed', tone: 'up', dot: 'ready' };
}

/**
 * The Lyrics section of the track page: where the words came from and how
 * they fitted, the first lines so a wrong song is obvious, and for the owner
 * of a ready track the two fixes, "Wrong lyrics?" (a relyric) and the editor.
 * A relyric in flight shows here; the track stays playable with its old
 * words until the new ones land.
 */
export function TrackLyricsPanel({ track, owned, song }: TrackLyricsPanelProps) {
  const preview = useLyricsPreview(track._id);
  const job = useTrackJob(owned ? track._id : undefined);
  const [fixing, setFixing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rebuild: Doc<'jobs'> | null = job?.kind === 'relyric' ? job : null;
  const rebuilding = rebuild?.status === 'queued' || rebuild?.status === 'processing';
  const failed = rebuild?.status === 'failed' ? (rebuild.error ?? 'unknown error') : null;
  const chip = status(track, rebuilding);
  const canFix = owned && track.processingStatus === 'ready' && !rebuilding;

  async function retry() {
    setBusy(true);
    setError(null);
    const result = await toResult(() => convex.mutation(api.tracks.relyric, { trackId: track._id }));
    setBusy(false);
    if (!result.ok) setError(result.error.message);
  }

  return (
    <section className="mt-[26px] border-t border-line pt-[18px]">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-[15px] font-semibold">Lyrics</h2>
        <MonoLabel tone={chip.tone} className="inline-flex items-center gap-2">
          <StatusDot tone={chip.dot} />
          {chip.label}
        </MonoLabel>
      </div>
      <p className="mt-1 max-w-[46ch] text-[13px] leading-[1.5] text-mute">
        {rebuilding
          ? `Fetching the words for ${song} and timing them against the vocal. The old words stay until these land.`
          : describeSource(track.lyricsSource, track.hasLyrics, song)}
      </p>
      {preview && preview.total > 0 && (
        <div className="mt-3 border-l-2 border-line2 pl-3 text-sm leading-[1.55] text-mute">
          {preview.lines.map((line, index) => (
            <div key={index} className={index === 0 ? 'text-ink' : undefined}>
              {line}
            </div>
          ))}
          {preview.total > preview.lines.length && (
            <div className="text-faint">… {preview.total - preview.lines.length} more lines</div>
          )}
        </div>
      )}
      {failed && !rebuilding && (
        <p className="mt-2 max-w-[46ch] text-[13px] leading-[1.5] text-down">
          The rebuild did not finish: {failed}. The words above are unchanged.
        </p>
      )}
      {owned && track.processingStatus === 'ready' && (
        <div className="mt-[10px] flex items-baseline gap-4">
          {failed ? (
            <button type="button" onClick={() => void retry()} disabled={!canFix || busy} className="text-[13px] text-acc hover:underline disabled:opacity-50">
              Try again
            </button>
          ) : (
            <button type="button" onClick={() => setFixing(true)} disabled={!canFix} className="text-[13px] text-acc hover:underline disabled:opacity-50">
              Wrong lyrics?
            </button>
          )}
          <Link
            to="/tracks/$trackId/lyrics"
            params={{ trackId: track._id }}
            disabled={!canFix}
            className="text-[13px] text-mute hover:text-ink aria-disabled:pointer-events-none aria-disabled:opacity-50"
          >
            Edit words
          </Link>
        </div>
      )}
      {error && <p className="mt-2 text-[13px] text-down">{error}</p>}
      {fixing && (
        <FixLyricsModal trackId={track._id} song={song} durationMs={track.durationMs} onClose={() => setFixing(false)} />
      )}
    </section>
  );
}
